import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import { BrandStatsForDomainResponse } from '../../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { getChartThemeColors } from '../../../../../shared/utils/chartTheme';
import { useTheme } from '../../../../../shared/contexts/theme/ThemeContext';
import { KeywordsData } from '../../../../../shared/models/tags/TagsResponse';
import './BrandMentionsChart.scss';

const MAX_BRANDS = 7;

interface BrandMentionsChartProps {
  brandsStats: BrandStatsForDomainResponse | null;
  isLoading: boolean;
  commonKeywords: KeywordsData[];
}

const COVERAGE_COLOR = '#1b5ff8';
const MENTIONS_COLOR = '#53cba2';

const BrandMentionsChart: React.FC<BrandMentionsChartProps> = ({
  brandsStats,
  isLoading,
  commonKeywords,
}) => {
  const { t } = useTranslation();
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);

  const hasData = brandsStats && brandsStats.brands.length > 0;

  const brandsMap = useMemo(() => {
    const map = new Map<string, string>();
    commonKeywords.forEach((k) => map.set(k.normalizedKeyword, k.keyword));
    return map;
  }, [commonKeywords]);

  const brandOptions = useMemo(() => {
    if (!brandsStats || brandsStats.brands.length === 0) return [];
    return brandsStats.brands.map((b) => ({
      label: brandsMap.get(b.brand) ?? b.brand,
      value: b.brand,
    }));
  }, [brandsStats, brandsMap]);

  const { theme } = useTheme();
  const filteredBrands = useMemo(() => {
    if (!brandsStats || brandsStats.brands.length === 0) return [];

    const sourceBrands =
      selectedBrands.length > 0
        ? brandsStats.brands.filter((b) => selectedBrands.includes(b.brand))
        : brandsStats.brands.slice(0, MAX_BRANDS);

    return sourceBrands.map((b) => ({
      ...b,
      brand: brandsMap.get(b.brand) ?? b.brand,
    }));
  }, [brandsStats, brandsMap, selectedBrands]);

  const chartOption = useMemo(() => {
    if (!hasData || filteredBrands.length === 0) return null;

    const brands = filteredBrands;
    const brandNames = brands.map((b) => b.brand);
    const maxMentions = Math.max(...brands.map((b) => b.mentionCount), 1);
    const chartColors = getChartThemeColors();

    return {
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        borderRadius: 8,
        padding: [8, 12],
        borderColor: chartColors.tooltipBorder,
        backgroundColor: chartColors.tooltipBg,
        textStyle: {
          color: chartColors.tooltipText,
          fontFamily: 'Inter,sans-serif',
          fontSize: 12,
        },
        formatter: (params: any) => {
          const brand = params[0]?.axisValue || '';
          const brandIdx = brands.findIndex((b) => b.brand === brand);
          const lines = params.map((p: any) => {
            const isCoverage = p.seriesName === t('coverage');
            const displayValue = isCoverage
              ? `${p.value.toFixed(1)}%`
              : brandIdx >= 0
              ? String(brands[brandIdx].mentionCount)
              : String(p.value);
            return `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px;"></span>${p.seriesName}: <b>${displayValue}</b>`;
          });
          return `<b>${brand}</b><br/>${lines.join('<br/>')}`;
        },
      },
      legend: {
        show: true,
        bottom: 0,
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 24,
        textStyle: {
          fontFamily: 'Inter,sans-serif',
          fontSize: 12,
          color: chartColors.legendText,
        },
      },
      grid: {
        top: 10,
        bottom: 40,
        left: 10,
        right: 30,
        containLabel: true,
      },
      xAxis: {
        type: 'value' as const,
        min: 0,
        max: 100,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: 'dashed' as const, color: chartColors.splitLineDashed },
        },
        axisLabel: { fontSize: 10, color: chartColors.axisLabelSecondary, formatter: '{value}%' },
      },
      yAxis: {
        type: 'category' as const,
        inverse: true,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          fontSize: 11,
          fontFamily: 'Inter,sans-serif',
          color: chartColors.tooltipText,
          width: 120,
          overflow: 'truncate' as const,
        },
        data: brandNames,
      },
      series: [
        {
          name: t('coverage'),
          type: 'bar' as const,
          barWidth: 16,
          barGap: '20%',
          barCategoryGap: '50%',
          itemStyle: {
            color: COVERAGE_COLOR,
            borderRadius: [0, 2, 2, 0],
          },
          data: brands.map((b) => b.coverage),
        },
        {
          name: t('mentions'),
          type: 'bar' as const,
          barWidth: 16,
          itemStyle: {
            color: MENTIONS_COLOR,
            borderRadius: [0, 2, 2, 0],
          },
          data: brands.map((b) =>
            maxMentions > 0 ? Math.round((b.mentionCount / maxMentions) * 100 * 10) / 10 : 0
          ),
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBrands, hasData, t, theme]);

  const chartHeight = useMemo(() => {
    if (!hasData || filteredBrands.length === 0) return '200px';
    return `${Math.max(filteredBrands.length * 55 + 60, 200)}px`;
  }, [filteredBrands, hasData]);

  return (
    <div className="stats__panel brand-mentions-chart">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('brandMentionsPerSource')}
          <CustomTooltip tooltipText="brandMentionsPerSourceTooltip" addMargin={false} />
        </div>
        {hasData && (
          <div className="brand-mentions-chart__filter">
            <MultiSelect
              value={selectedBrands}
              onChange={(e) => setSelectedBrands(e.value || [])}
              options={brandOptions}
              optionLabel="label"
              optionValue="value"
              className="dropdown dropdown--multi-select"
              maxSelectedLabels={1}
              selectionLimit={MAX_BRANDS}
              showSelectAll={false}
              placeholder={t('selectBrands')}
              filter
              filterBy="label"
              panelFooterTemplate={() => (
                <div className="domains-filter__footer">
                  <Button
                    label={t('resetToDefault')}
                    className="btn btn--sm btn--primary"
                    onClick={() => setSelectedBrands([])}
                    disabled={selectedBrands.length === 0}
                  />
                </div>
              )}
            />
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="stats-box stats-box--bar-chart">
          <div className="stats-box__content">
            <Skeleton width="100%" height="200px" />
          </div>
        </div>
      ) : !hasData ? (
        <div className="stats-box stats-box--bar-chart stats-box--empty">
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        </div>
      ) : (
        <div className="stats-box stats-box--bar-chart">
          <div className="stats-box__content stats-box__content--chart-tight">
            <ReactECharts
              key={theme}
              option={chartOption!}
              style={{ height: chartHeight, width: '100%' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandMentionsChart;
